/** Shared dropdown panel + item styles (nav bar, ACTIONS, Find, breadcrumb). */

import { grey } from "@mui/material/colors";

/**
 * @figma Pattern 150 · List Toolbar spacing and Button/Label type.
 * Controls sit 18px apart on sm+: 5px margin each side plus the 8px gap
 * GridToolbarContainer (MUI X) already puts between its children. Label is Roboto 12/16 bold,
 * 0.8px tracking; icons are 20px with an 8px gap to the label.
 * Used by actionsMenuButtonSx below and listToolbarButtonSx (Buttons.tsx).
 */
export const LIST_TOOLBAR_HALF_GAP = "5px";
export const listToolbarLabelSx = {
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: "16px",
  letterSpacing: "0.8px",
  "& .MuiButton-startIcon": { ml: 0, mr: "8px" },
  "& .MuiButton-endIcon": { ml: "8px", mr: 0 },
  "& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root": {
    fontSize: "20px",
  },
};

/** Panel bg — nav + ACTIONS + Find + breadcrumb menus. */
export const DROPDOWN_MENU_BG = "#fafbfb";
/** Cool blue-grey hover — ACTIONS / Find (text stays grey). */
export const DROPDOWN_MENU_HOVER = "rgba(212, 224, 225)";
/** Nav dropdown hover pill — light teal. */
export const NAV_DROPDOWN_HOVER_BG = "rgba(1, 138, 144, 0.12)";
/** Nav dropdown hover / active text — teal. */
export const NAV_DROPDOWN_HOVER_TEXT = "#018a90";
/** Menu item text — option labels (nav / ACTIONS / Find / ⋮). */
export const DROPDOWN_MENU_TEXT = "#898b8e";
/** House icon + section title in dropdown headers. */
export const NAV_DROPDOWN_HEADER_COLOR = "#6D6B69";
/** Soft diffuse shadow — matches Find field dropdown. */
export const DROPDOWN_MENU_SHADOW =
  "0 8px 24px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.1)";
/** Shared left edge — house icon, option first letter, title grey rule. */
export const NAV_DROPDOWN_CONTENT_INSET = 1.5;
/** Top-bar notification / help / logout — match Aeronet Brain. */
export const TOP_BAR_ICON_COLOR = "#ffffff";
export const TOP_BAR_ICON_SIZE = 23;
export const topBarIconButtonSx = {
  color: TOP_BAR_ICON_COLOR,
  p: 0.75,
};
export const topBarIconSx = {
  fontSize: TOP_BAR_ICON_SIZE,
};

/** Scroll overflowing menus without painting a scrollbar. */
export const hiddenScrollbarSx = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": {
    width: 0,
    height: 0,
    display: "none",
  },
};

export const dropdownMenuPaperSx = {
  backgroundColor: `${DROPDOWN_MENU_BG} !important`,
  background: `${DROPDOWN_MENU_BG} !important`,
  opacity: "1 !important",
  // ACTIONS / Find — 12px; nav/submenu override to 8px
  borderRadius: "12px !important",
  boxShadow: `${DROPDOWN_MENU_SHADOW} !important`,
  mt: 0.5,
  // ACTIONS / Find — size to content; nav/submenu override to fixed width
  width: "max-content",
  minWidth: "unset !important",
  maxWidth: "none",
  // 1 (8px) L/R — equal gap from hover pill to panel edge (same as nav)
  pt: 1.5,
  pb: 2,
  px: 1,
  maxHeight: "calc(100vh - 96px)",
  overflowX: "hidden" as const,
  overflowY: "auto" as const,
  ...hiddenScrollbarSx,
  // ACTIONS Print (etc.): air after section rule → ListSubheader (same 3px as nav
  // title-rule → first option). MenuItem-after-rule stays on navDropdownMenuPaperSx
  // so Help Hub is unaffected.
  "& .MuiList-root > [aria-hidden] + .MuiListSubheader-root": {
    marginTop: "3px",
  },
};

export const dropdownMenuListSx = {
  py: 0,
  width: "100%",
  minWidth: "max-content",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
};

/** Fixed nav panel width — parent + cascading submenu must match.
 * Sized for longest label ("Work Orders Responsive" @ 15px Roboto-Medium)
 * + left inset + chevron (~18px) + paper px: 1 both sides.
 */
export const NAV_DROPDOWN_PANEL_WIDTH = "210px";

/**
 * Horizontal gap between parent nav dropdown and cascading submenu.
 * Tight enough that background doesn't show through; leave room so papers
 * don't overlap the parent chevron (2px sat on the main menu).
 */
export const NAV_SUBMENU_OFFSET_ML = "8px";
/**
 * Invisible hover bridge left of submenu — covers the 8px visual gap plus a few px
 * into the parent row so the pointer never leaves “menu” while crossing.
 * Must be wider than NAV_SUBMENU_OFFSET_ML; paper overflow must not clip it.
 */
const NAV_SUBMENU_HOVER_BRIDGE_PX = 16;

/** Cascading sub-menu — fixed width (same as top-nav); sit to the right of the parent row. */
export const subMenuPaperSx = {
  ...dropdownMenuPaperSx,
  // Same 8px as shared ACTIONS/Find/nav paper
  borderRadius: "8px !important",
  // Fixed width — same as top-nav; do not grow with label
  width: NAV_DROPDOWN_PANEL_WIDTH,
  minWidth: `${NAV_DROPDOWN_PANEL_WIDTH} !important`,
  maxWidth: NAV_DROPDOWN_PANEL_WIDTH,
  // Explicit — same as shared paper (breadcrumb uses mx: 1 instead)
  px: 1,
  // Tight gap vs original 11px; 8px keeps papers from overlapping the chevron
  ml: `${NAV_SUBMENU_OFFSET_ML} !important`,
  marginLeft: `${NAV_SUBMENU_OFFSET_ML} !important`,
  position: "relative",
  // Allow ::before bridge to extend into the gap (shared paper sets overflow hidden)
  overflow: "visible",
  overflowX: "visible",
  overflowY: "visible",
  // Invisible hit area to the left (covers Popover gap / rounded-corner dead zone)
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    bottom: 0,
    left: -NAV_SUBMENU_HOVER_BRIDGE_PX,
    width: NAV_SUBMENU_HOVER_BRIDGE_PX,
    // HoverMenu sets pointer-events:none on the Menu root; bridge must still hit-test
    pointerEvents: "auto",
  },
  "& .MuiList-root": {
    ...dropdownMenuListSx,
    minWidth: 0,
    width: "100%",
  },
};

/** Top-nav menu — fixed width (same as submenu via NAV_DROPDOWN_PANEL_WIDTH). */
export const navDropdownMenuPaperSx = {
  ...dropdownMenuPaperSx,
  mt: "6px !important",
  marginTop: "6px !important",
  // Same 8px as shared ACTIONS/Find paper
  borderRadius: "8px !important",
  width: NAV_DROPDOWN_PANEL_WIDTH,
  minWidth: `${NAV_DROPDOWN_PANEL_WIDTH} !important`,
  maxWidth: NAV_DROPDOWN_PANEL_WIDTH,
  // Explicit — same as shared paper (breadcrumb uses mx: 1 instead)
  px: 1,
  pt: 1.5,
  pb: 2,
  "& .MuiList-root": {
    ...dropdownMenuListSx,
    minWidth: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  "& .MuiDivider-root": {
    width: "100%",
    borderColor: "#e0e0e0",
  },
  // Shorter rows would pull the first label closer to the title rule.
  // Restore that offset only on the option immediately after the divider
  // (header is a MenuItem too, so :first-of-type is the title, not Address Book).
  "& .MuiList-root > [aria-hidden] + .MuiMenuItem-root": {
    marginTop: "3px",
  },
};

/**
 * Help Center panel — same chrome as nav, 28px option rows (nav compact pitch).
 * Title → first option: keep a clear gap after the header rule (do not squash).
 * Section divider (Whats New / Hub) must not inherit this offset.
 */
export const helpDropdownMenuPaperSx = {
  ...navDropdownMenuPaperSx,
  width: NAV_DROPDOWN_PANEL_WIDTH,
  minWidth: `${NAV_DROPDOWN_PANEL_WIDTH} !important`,
  maxWidth: NAV_DROPDOWN_PANEL_WIDTH,
  borderRadius: "4px !important",
  px: 1,
  pt: 1,
  pb: 1,
  // Kill nav’s 3px after every aria-hidden rule (would pad Hub too).
  "& .MuiList-root > [aria-hidden] + .MuiMenuItem-root": {
    marginTop: 0,
  },
  "& .MuiList-root > .help-title-rule + .MuiMenuItem-root": {
    marginTop: "5px",
  },
};

/** Shared PaperProps for Menu (ACTIONS / Find / etc.). */
export const dropdownMenuPaperProps = {
  elevation: 0 as const,
  sx: dropdownMenuPaperSx,
};

/** Shared MenuListProps for Menu. */
export const dropdownMenuListProps = {
  dense: true as const,
  disablePadding: true as const,
  sx: dropdownMenuListSx,
};

/** Compact row — 15px Roboto-Medium; row height matches original nav hover pill. */
export const dropdownMenuItemSx = {
  fontSize: "15px",
  fontFamily: '"Roboto-Medium", "Roboto", sans-serif !important',
  fontWeight: 400,
  color: `${DROPDOWN_MENU_TEXT} !important`,
  py: 0.5,
  px: 2,
  borderRadius: 0,
  mx: 0,
  my: 0,
  minHeight: "32px !important",
  height: "32px !important",
  minWidth: "unset !important",
  width: "auto",
  whiteSpace: "nowrap",
  lineHeight: 1.25,
  transition: "background-color 0.12s",
  "&:hover": {
    backgroundColor: `${DROPDOWN_MENU_HOVER} !important`,
    color: `${DROPDOWN_MENU_TEXT} !important`,
  },
  "&.Mui-selected": {
    backgroundColor: `${DROPDOWN_MENU_HOVER} !important`,
    color: `${DROPDOWN_MENU_TEXT} !important`,
    fontWeight: 400,
  },
  "&.Mui-selected:hover": {
    backgroundColor: `${DROPDOWN_MENU_HOVER} !important`,
    color: `${DROPDOWN_MENU_TEXT} !important`,
  },
  "& .MuiTypography-root": {
    color: "inherit !important",
    fontSize: "inherit",
    fontFamily: "inherit",
    lineHeight: "inherit",
  },
  "& a": {
    color: "inherit !important",
  },
};

/**
 * Top-nav / ACTIONS / Find / ⋮ items — teal hover pill.
 * Paper px: 1 is the gap from the white box; item stretches inside
 * that inset (no extra mx).
 */
export const navDropdownMenuItemSx = {
  ...dropdownMenuItemSx,
  mx: 0,
  // Item-to-item: no extra gutter (was my: 0.125 → 2px between pills).
  // First option after the header divider keeps offset via paper rule below.
  my: 0,
  py: 0.25,
  minHeight: "28px !important",
  height: "28px !important",
  pl: NAV_DROPDOWN_CONTENT_INSET,
  pr: 2,
  borderRadius: "8px",
  display: "flex",
  width: "100%",
  maxWidth: "100%",
  alignSelf: "stretch",
  boxSizing: "border-box",
  // Panel is wide enough for full labels (nowrap); no ellipsis truncation
  "& .MuiTypography-root": {
    color: "inherit !important",
    fontSize: "inherit",
    fontFamily: "inherit",
    lineHeight: "inherit",
    whiteSpace: "nowrap",
  },
  "&:hover": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
  },
  "&.Mui-selected": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
    fontWeight: 400,
  },
  "&.Mui-selected:hover": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
  },
};

/**
 * Breadcrumb panel — same bg/shadow as nav; fixed width like nav menus.
 * mt: 0 + ::before bridge so hover can move from trigger → menu without closing.
 * No house-title header: panel pt/pb = 8px so top/bottom inset to the hover pill
 * matches left/right (item mx: 1 with paper px: 0).
 */
export const breadcrumbDropdownMenuPaperSx = {
  ...dropdownMenuPaperSx,
  mt: "0 !important",
  marginTop: "0 !important",
  // Same 12px as ACTIONS/Find shared paper (nav overrides to 8px)
  borderRadius: "12px !important",
  width: "220px",
  minWidth: "220px !important",
  maxWidth: "220px",
  // Side gap comes from item mx: 1 (8px); paper has no horizontal pad
  px: 0,
  // Match L/R highlight inset (8px); don't use shared/nav pt: 1.5
  pt: 1,
  pb: 1,
  position: "relative",
  // Catch the mouse while crossing from the title into the panel
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    top: -12,
    left: 0,
    right: 0,
    height: 12,
  },
  // Kill item my: 0.5 on outer edges so paper pt/pb alone is the inset (no 8+4 stack)
  "& .MuiList-root > .MuiMenuItem-root:first-of-type": {
    marginTop: "0 !important",
  },
  "& .MuiList-root > .MuiMenuItem-root:last-of-type": {
    marginBottom: "0 !important",
  },
};

/**
 * Breadcrumb rows — black title; teal hover pill with side gap via mx.
 * Overrides nav item width:100% so mx actually insets the highlight.
 * mx: 1 (8px) — breadcrumb switcher spacing (same visual gap as top-nav paper px: 1).
 */
export const breadcrumbDropdownMenuItemSx = {
  ...navDropdownMenuItemSx,
  color: "#000000 !important",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  py: 1.25,
  pl: 2.5,
  pr: 5,
  mx: 1,
  my: 0.5,
  minWidth: "unset !important",
  width: "auto",
  alignSelf: "stretch",
  minHeight: "unset !important",
  height: "auto !important",
  whiteSpace: "normal",
  lineHeight: 1.25,
  "&:hover": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
  },
  "&.Mui-selected": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
    fontWeight: 400,
  },
  "&.Mui-selected:hover": {
    backgroundColor: `${NAV_DROPDOWN_HOVER_BG} !important`,
    color: `${NAV_DROPDOWN_HOVER_TEXT} !important`,
  },
  "& .breadcrumb-menu-desc, &:hover .breadcrumb-menu-desc, &.Mui-selected .breadcrumb-menu-desc, &.Mui-selected:hover .breadcrumb-menu-desc":
    {
      color: "#616161 !important",
      fontSize: "12px !important",
      fontFamily: '"Roboto-Regular", "Roboto", sans-serif !important',
      fontWeight: "400 !important",
    },
};

/**
 * Inventory breadcrumb rows — taller teal pill + grey title/description.
 */
export const breadcrumbDropdownMenuItemSxInventory = {
  ...breadcrumbDropdownMenuItemSx,
  color: "#616161 !important",
  py: 0.6,
  my: 0.5,
  lineHeight: 1.2,
  "& .breadcrumb-menu-desc, &:hover .breadcrumb-menu-desc, &.Mui-selected .breadcrumb-menu-desc, &.Mui-selected:hover .breadcrumb-menu-desc":
    {
      color: "#898b8e !important",
      fontSize: "12px !important",
      fontFamily: '"Roboto-Regular", "Roboto", sans-serif !important',
      fontWeight: "400 !important",
    },
};

/** Home + section title row under the black AppBar (non-interactive). */
export const navDropdownHeaderSx = {
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
  boxSizing: "border-box",
  fontSize: "15px",
  fontFamily: '"Roboto-Bold", "Roboto", sans-serif !important',
  fontWeight: 400,
  color: `${NAV_DROPDOWN_HEADER_COLOR} !important`,
  opacity: "1 !important",
  py: 1,
  px: NAV_DROPDOWN_CONTENT_INSET,
  mx: 0,
  my: 0,
  minHeight: "unset !important",
  height: "auto !important",
  gap: 1,
  cursor: "default",
  pointerEvents: "none",
  // Same 8px pill as navDropdownMenuItemSx. When hovered in Menu.tsx
  // this link row renders with a rounded pill matching leaf items.
  borderRadius: "8px",
  // Kill MenuItem focus/selected grey (shows on first open)
  backgroundColor: "transparent !important",
  "&:hover, &:focus, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible:hover":
    {
      backgroundColor: "transparent !important",
    },
  "&.Mui-disabled": {
    opacity: "1 !important",
  },
  "& .MuiTypography-root": {
    color: "inherit !important",
    fontSize: "inherit",
    fontFamily: "inherit",
    lineHeight: 1.2,
    pt: "2px",
  },
};

/** Same as `navDropdownHeaderSx` — headers stay grey even when the section is active. */
export const navDropdownHeaderActiveSx = navDropdownHeaderSx;

/**
 * House icon in nav / Help headers — larger than body text; roof sits
 * above the title cap height (flex-start + slight negative top margin).
 */
export const navDropdownHomeIconSx = {
  fontSize: 22,
  color: "inherit",
  flexShrink: 0,
  mt: "-1px",
  alignSelf: "flex-start",
};

/**
 * Header rule — starts at the house icon / first letter of the options,
 * same inset on the right (not full-bleed to the panel edge).
 */
export const navDropdownDividerSx = {
  listStyle: "none",
  my: 0,
  mx: NAV_DROPDOWN_CONTENT_INSET,
  p: 0,
  width: "auto",
  alignSelf: "stretch",
  border: "none",
  borderBottom: "1px solid #e0e0e0",
  display: "block",
  flexShrink: 0,
  boxSizing: "border-box",
};

/** Help options — 28px pitch (same as nav compact; not the old 24px rows). */
export const helpDropdownMenuItemSx = {
  ...navDropdownMenuItemSx,
  py: 0.25,
  minHeight: "28px !important",
  height: "28px !important",
};

/**
 * Section rule between Whats New and Hub (not the title rule).
 * 8px above + 8px below the 1px line — more air than option-to-option.
 */
export const helpDropdownSectionDividerSx = {
  ...navDropdownDividerSx,
  my: 1,
};

export const dropdownMenuListItemTextSx = {
  m: 0,
  flex: "1 1 auto",
  width: "100%",
  "& .MuiTypography-root": {
    fontSize: "inherit",
    fontWeight: "inherit",
    fontFamily: "inherit",
    color: "inherit !important",
    lineHeight: "inherit",
    whiteSpace: "nowrap",
  },
};

/** Primary + secondary stack inside breadcrumb menu items. */
export const breadcrumbMenuItemTextSx = {
  m: 0,
  flex: "1 1 auto",
  "& .MuiListItemText-primary": {
    fontSize: "15px",
    fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
    fontWeight: 400,
    lineHeight: 1.3,
    color: "inherit",
  },
  "& .MuiListItemText-secondary": {
    fontSize: "12px",
    fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
    fontWeight: 400,
    lineHeight: 1.3,
    color: grey[500],
    mt: 0.25,
  },
};

/**
 * ACTIONS section label (e.g. "Print") — bold black, same left inset as options.
 * Kill MUI ListSubheader sticky + 48px line-height (was the huge gap under the rule).
 */
export const dropdownMenuSectionLabelSx = {
  opacity: "1 !important",
  backgroundColor: `${DROPDOWN_MENU_BG} !important`,
  position: "static",
  lineHeight: "28px",
  minHeight: "28px !important",
  height: "28px !important",
  py: 0,
  // Align with nav/ACTIONS option text (paper px: 1 + pl: 1.5)
  pl: NAV_DROPDOWN_CONTENT_INSET,
  pr: 2,
  mx: 0,
  my: 0,
  cursor: "default",
  fontSize: "15px",
  fontFamily: '"Roboto-Bold", "Roboto", sans-serif !important',
  fontWeight: 400,
  color: "#000000 !important",
  "& b, & strong": {
    fontFamily: "inherit !important",
    fontWeight: "normal !important",
  },
  "&:hover": {
    backgroundColor: `${DROPDOWN_MENU_BG} !important`,
  },
};

/**
 * Nested ACTIONS rows under a section label (Pull List, Parts Requests).
 * Same left inset as top-level options (Delete Demand, etc.) — do not indent further.
 */
export const dropdownMenuNestedItemSx = {
  ...navDropdownMenuItemSx,
};

/**
 * Detail / shared ACTIONS rows. List views use `listActionsDropdownMenuItemSx`.
 * (upstream 1a82588e4)
 */
export const actionsDropdownMenuItemSx = {
  ...navDropdownMenuItemSx,
  py: 0.5,
  pl: 2,
  pr: 2,
  minHeight: "32px !important",
  height: "32px !important",
};

/** Detail / shared ACTIONS panel. List views use `listActionsDropdownMenuPaperSx`. */
export const actionsDropdownMenuPaperSx = {
  ...dropdownMenuPaperSx,
  minWidth: "200px !important",
};

/**
 * List ACTIONS rows — Figma list menu only (upstream 1a82588e4).
 * 32px, 15px medium grey, 8px teal hover pill. Detail menus do not use this.
 * A row that shows a disabled reason still grows through `dropdownMenuItemSxFor`.
 */
export const listActionsDropdownMenuItemSx = {
  ...actionsDropdownMenuItemSx,
  py: "4px",
  lineHeight: "19px",
  fontWeight: 500,
};

/** List ACTIONS section label (Print). 28px bold black. */
export const listActionsDropdownSectionLabelSx = {
  ...dropdownMenuSectionLabelSx,
  pl: 2,
  fontWeight: 700,
};

/** Rule above a list ACTIONS section. Full panel width, 4px above, 12px below. */
export const listActionsDropdownSectionDividerSx = {
  listStyle: "none",
  display: "block",
  flexShrink: 0,
  boxSizing: "border-box",
  border: "none",
  borderBottom: "1px solid #e0e0e0",
  mx: -1,
  mt: "4px",
  mb: "12px",
  p: 0,
  height: 0,
  width: "auto",
  alignSelf: "stretch",
};

/** List ACTIONS panel. Detail menus keep `actionsDropdownMenuPaperSx`. */
export const listActionsDropdownMenuPaperSx = {
  ...actionsDropdownMenuPaperSx,
  "& .MuiList-root > [aria-hidden] + .MuiListSubheader-root": {
    marginTop: 0,
  },
};

/**
 * The list ACTIONS panel. Solid white and raised (d0336b4a8, 2026-09-24): the shared
 * panel's translucent background let the grid show through the open menu.
 */
export const listActionsDropdownMenuPaperProps = {
  elevation: 3 as const,
  sx: {
    ...listActionsDropdownMenuPaperSx,
    backgroundColor: "#ffffff !important",
    background: "#ffffff !important",
    opacity: "1 !important",
  },
};

export const actionsDropdownMenuPaperProps = {
  elevation: 0 as const,
  sx: actionsDropdownMenuPaperSx,
};

/**
 * A disabled item that says WHY (Pattern 340 §2b): its `disabledReason` is a second,
 * smaller line BENEATH the label. The rows above are a fixed 28px, so a two-line item
 * spilled its reason over its own label and the next item's (Aaron 2026-10-11, the Log
 * Book tab's Add New). Such a row grows to fit both lines; the reason wraps at a readable
 * width instead of stretching the panel. Shared by CreateMenu, ActionMenu and
 * RecordActionsKebab through `dropdownMenuItemSxFor`; a caller never styles it.
 */
export const DROPDOWN_MENU_REASON_MAX_WIDTH = "320px";
export const dropdownMenuReasonRowSx = {
  height: "auto !important",
  minHeight: "28px !important",
  py: 0.5,
  alignItems: "flex-start",
  // Three classes deep, so it wins over the row's and the text's own Typography rules
  // (each two deep) whichever of them emotion inserted last.
  "& .MuiListItemText-root .MuiListItemText-primary": {
    lineHeight: "20px",
  },
  "& .MuiListItemText-root .MuiListItemText-secondary": {
    display: "block",
    mt: "2px",
    fontSize: "12px",
    lineHeight: "16px",
    whiteSpace: "normal",
    maxWidth: DROPDOWN_MENU_REASON_MAX_WIDTH,
  },
};

/** A menu row's sx: `itemSx`, grown to two lines when the row shows a reason. */
export function dropdownMenuItemSxFor<T extends object>(itemSx: T, reason?: string | null) {
  return reason ? { ...itemSx, ...dropdownMenuReasonRowSx } : itemSx;
}

/**
 * Section rule between top-level ACTIONS and a Print (or similar) block.
 * `my: 0` like nav title rule; `mt: 3px` adds air after the prior option so the
 * gap above the border matches the paper’s 3px after-rule → first row below.
 */
export const dropdownMenuSectionDividerSx = {
  ...navDropdownDividerSx,
  my: 0,
  mt: "3px",
};

/*
 * The status pill's menu (StatusPillMenu, Figma Component 63 Chevron=true): the
 * inline status edit in a grid row. R55 hand reconcile from gitlab/develop
 * ca17f2a94 (this file is LOCAL-CONTENT, so only these three were taken). Upstream
 * builds them on its list-ACTIONS chain (actionsDropdownMenuItemSx →
 * listActionsDropdownMenuItemSx, actionsDropdownMenuPaperSx →
 * listActionsDropdownMenuPaperSx), which local main does not carry; the values
 * below are upstream's, with that chain flattened in.
 */
/** Status pill menu rows: 32px, 15px medium grey, teal hover pill, 4px between rows. */
export const statusDropdownMenuItemSx = {
  ...navDropdownMenuItemSx,
  py: "4px",
  pl: 2,
  pr: 2,
  minHeight: "32px !important",
  height: "32px !important",
  lineHeight: "19px",
  fontWeight: 500,
  marginTop: "0 !important",
  marginBottom: "0 !important",
  "& + &": {
    marginTop: "4px !important",
  },
};

export const statusDropdownMenuListSx = {
  ...dropdownMenuListSx,
  display: "flex",
  flexDirection: "column",
};

/** Status pill menu panel: the shared panel at a fixed 200px, 6px under the pill. */
export const statusDropdownMenuPaperSx = {
  ...dropdownMenuPaperSx,
  "& .MuiList-root > [aria-hidden] + .MuiListSubheader-root": {
    marginTop: 0,
  },
  width: "200px !important",
  minWidth: "200px !important",
  maxWidth: "200px !important",
  mt: "6px",
};

/** Breadcrumb current page trigger (e.g. "Parts ▾"). */
export const breadcrumbCurrentTriggerSx = {
  textTransform: "none",
  p: 0,
  pl: 0,
  minWidth: 0,
  minHeight: "unset",
  height: "auto",
  lineHeight: "inherit",
  verticalAlign: "baseline",
  color: "inherit",
  display: "inline-flex",
  alignItems: "baseline",
  fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
  fontSize: "24px",
  "&:hover": { backgroundColor: "transparent" },
  "& .MuiButton-endIcon": {
    ml: 0.25,
    mr: 0,
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "baseline",
    fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
  },
};

export const breadcrumbCurrentLabelSx = {
  fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
  fontWeight: 400,
  fontSize: "24px",
  lineHeight: "inherit",
  display: "inline",
};

/** Keep Inventory + current crumb on one baseline. */
export const breadcrumbTitleSx = {
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: 0,
  fontSize: "24px",
};

/** Parent crumb (module name) before the current segment. */
export const breadcrumbModuleLinkSx = {
  fontFamily: '"Roboto-Light", "Roboto", sans-serif',
  fontWeight: 300,
  fontSize: "24px",
  pr: 0,
};

export const breadcrumbChevronSx = {
  fontFamily: '"Roboto-SemiBold", "Roboto", sans-serif',
  fontSize: "1.35em",
  "& path": {
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinejoin: "round",
  },
};

/**
 * Contained ACTIONS toolbar button (detail / grid tabs).
 * Change here once — all local ActionMenuButton copies pick it up.
 * @figma Component 22 · Button — Action Dropdown. PRODUCTION (gitlab/develop, 32px
 * height, 7px/13px padding, 13px/600 type) is the reference since Aaron's R55-Q1 ruling
 * 2026-10-06; the Figma node still shows 36px / 13-10 and is a design follow-up.
 */
export const actionsMenuButtonSx = {
  color: { sm: "white" },
  mx: { xs: 1, sm: 1.5 },
  height: "32px",
  minHeight: "32px",
  borderRadius: "4px",
  minWidth: "32px",
  padding: "7px 13px",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: 1.2,
  textTransform: "uppercase",
  backgroundColor: { sm: grey[500] },
  // Flat like FILTERS — MUI contained defaults add elevation
  boxShadow: "none",
  "&:hover": {
    backgroundColor: { sm: grey[700] },
    boxShadow: (theme) => theme.shadows[4],
  },
};

/**
 * Top-nav bar dropdown — the nav link anchor (bottom at 45px) sits 7.5px higher
 * than the top-bar icon buttons (bottom at 52.5px), so it needs a larger offset
 * to land at the same overlap over the black AppBar. ponytail: measured against
 * AppBar height 70 / LOGO_HEIGHT 26 in Nav.tsx — retune if either changes.
 * (develop uses 16px: its nav row carries an extra translateY(2px).)
 */
export const navBarDropdownMenuPaperSx = {
  ...navDropdownMenuPaperSx,
  mt: "18px !important",
  marginTop: "18px !important",
};
